import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { AccountState, type Account } from '@/services/auth/types';
import { DeliverySettings, useAccountEmailSetup, useListWorkspaces, useSampleWorkspace } from '@/features/workspaces';
import { isEmptyString } from '@/utils/string';

export type EssentialsOnboardingGate = {
  /** The onboarding cohort: trial, non-builder, flag on. */
  isEligible: boolean;
  isComplete: boolean;
  isLoading: boolean;
};

/** Minimal workspace projection for the any-workspace "live" check. */
type WorkspaceDeliveryProjection = {
  cio_workspace_id: number;
  delivery_settings: DeliverySettings;
};

export type EssentialsOnboardingCompletionInput = {
  hasVerifiedSendingDomain: boolean;
  workspaces: ReadonlyArray<WorkspaceDeliveryProjection>;
  /** Identifies the injected sample workspace (excluded — hardcoded to `normal`). */
  isSampleWorkspace: (workspaceId: number | null | undefined) => boolean;
};

/**
 * Whether a workspace is out of test mode (may send to its audience). Mirrors
 * the canonical test-mode gate in `delivery-mode-banner` and the workspace-setup
 * reconciler: test mode is `delivery_settings === SendToOne`; `Off` ("don't
 * send messages") is a separate delivery-disabled state, not test mode, and
 * counts as out of test mode there. An empty string is NOT out of test mode —
 * the wire type is `string`, so `''` (the factory default) survives the
 * `?? Normal` coalesce in `getMergedWorkspaceFromRaw` and would otherwise read
 * as live. Null/undefined get the same treatment (defensive — the coalesce
 * upstream already covers the real wire shape).
 */
export function isOutOfTestMode(deliverySettings: string | null | undefined): boolean {
  if (deliverySettings == null || isEmptyString(deliverySettings)) {
    return false;
  }
  return deliverySettings !== DeliverySettings.SendToOne;
}

/**
 * Onboarding is complete when the account has a verified sending domain AND
 * any non-sample workspace is out of test mode. Known imprecision (verified
 * domain in workspace A + live workspace B) is acceptable at trial scale.
 */
export function isEssentialsOnboardingComplete(input: EssentialsOnboardingCompletionInput): boolean {
  if (!input.hasVerifiedSendingDomain) {
    return false;
  }
  return input.workspaces.some(
    (w) => !input.isSampleWorkspace(w.cio_workspace_id) && isOutOfTestMode(w.delivery_settings),
  );
}

/**
 * Single cohort decision for the essentials onboarding takeover. While
 * `isLoading` is true, `isEligible`/`isComplete` are provisional — callers
 * must not act on them until it settles.
 *
 * Composes `useAccountEmailSetup` (verified domain) + `useListWorkspaces` (any
 * non-sample workspace out of test mode). Both queries are gated on the
 * synchronously computed cohort rule so ineligible accounts fire zero requests.
 *
 * `enabled: false` suppresses the fetch, not the read: an ineligible account
 * whose cache was warmed by another caller (e.g. the authenticated layout
 * mounts `useListWorkspaces` for every account) gets `isComplete: true` out of
 * this hook. Consumers must guard on `isEligible` before acting on
 * `isComplete` — `selectHomeScreen` and `selectOnboardingRedirect` do.
 *
 * A terminal query error fails open: the hook settles with `isComplete: true`
 * and `isLoading: false` rather than holding forever. Holding would strand the
 * user on a blank home (`selectHomeScreen` → 'loading' → `null`) or a frozen
 * onboarding loader with no recovery (`refetchOnWindowFocus` is off and 4xx
 * never retries). Failing open hands the eligible cohort back to the standard
 * home / redirects off `/onboarding/*` — a usable, recoverable screen — instead
 * of a dead end. The decision is deliberately "do not take over on a read we
 * can't trust," not "regress to the checklist."
 */
export function useEssentialsOnboardingGate(): EssentialsOnboardingGate {
  const isFlagEnabled = useFeatureFlag(AccountFeatureFlag.EssentialsOnboarding);
  const { account } = useAuth();

  const isEligible = isEligibleForOnboarding({ isFlagEnabled: isFlagEnabled === true, account });

  const emailSetup = useAccountEmailSetup({ enabled: isEligible });
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaces = useListWorkspaces({
    enabled: isEligible,
    select: (data): WorkspaceDeliveryProjection[] =>
      data.map((w) => ({ cio_workspace_id: w.cio_workspace_id, delivery_settings: w.delivery_settings })),
  });

  const isQueryError = emailSetup.isError || workspaces.isError;
  const hasVerifiedSendingDomain = emailSetup.data?.hasVerifiedSendingDomain ?? false;
  const isComplete = isQueryError
    ? true
    : isEssentialsOnboardingComplete({
        hasVerifiedSendingDomain,
        workspaces: workspaces.data ?? [],
        isSampleWorkspace,
      });

  return {
    isEligible,
    isComplete,
    isLoading: isEligible && (emailSetup.isLoading || workspaces.isLoading),
  };
}

export type EssentialsOnboardingEligibilityInput = {
  isFlagEnabled: boolean;
  account: Pick<Account, 'account_state' | 'plan_slug'>;
};

/**
 * Who gets onboarding at all: a trial account on a non-builder plan, with the
 * flag on. Deliberately says nothing about the signup answers — the wizard
 * exists to ask what someone wants to do, so gating it on having already said
 * would be circular.
 */
export function isEligibleForOnboarding(input: EssentialsOnboardingEligibilityInput): boolean {
  const { isFlagEnabled, account } = input;
  return isFlagEnabled && account.account_state === AccountState.Free && !isBuilderPlan(account);
}
